import _ from 'lodash';
import moment from 'moment';

import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { ShootingContext } from 'contexts';
import { makeStyles } from '@material-ui/core/styles';
import { Drawer, Button, Box, Typography, CircularProgress } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import PhotoCameraOutlinedIcon from '@material-ui/icons/PhotoCameraOutlined';
import ImageOutlinedIcon from '@material-ui/icons/ImageOutlined';
import ErrorOutlineRoundedIcon from '@material-ui/icons/ErrorOutlineRounded';
import { ServiceOffer, Shooting } from 'models';
import { showModal, hideSideBar } from 'state/actions';
import { fetchLongDistanceServiceOffer } from 'apis';
import { useAuth, useBlueBody, useDistanceFee, useLanguage, useShooting } from 'hooks';
import { ENG, MODAL, COLOR, SIDEBAR } from 'config/constants';
import { images } from 'assets/images';
import { HvPleaseWait } from 'components';
import { LOADING } from 'state/constants';

const useStyles = makeStyles({
  shootingDetail: {
    width: '100%',
    position: 'relative',
    boxSizing: 'border-box',
    padding: '30px 15px 15px',
    overflowY: 'auto',

    '@media (min-width: 600px)': {
      width: '400px',
    },

    '@media (max-width: 599px)': {
      width: '100vw',
    },
  },
  close: {
    position: 'absolute',
    top: 10,
    right: 10,
    minWidth: 'auto',
  },
  infoBlock: {
    marginBottom: 20,

    '&:last-child': {
      marginBottom: 0,
    },
  },
  infoBlockTitle: {
    fontSize: 12,
    fontWeight: 600,
    textTransform: 'uppercase',
    opacity: 0.5,
    marginBottom: 5,
  },
  infoItem: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  bolder: {
    fontWeight: 800,
  },
  bold: {
    fontWeight: 600,
  },
  value: {
    fontSize: 14,
  },
  messageBox: {
    textAlign: 'center',
    backgroundColor: '#f5f5fa',
    borderRadius: 5,
    padding: 20,
    margin: '50px 20px 20px',
  },
  icon: {
    width: 50,
    height: 50,
    color: COLOR.BLUE,
    margin: '0 auto',
  },
  message: {
    textAlign: 'left',
    marginTop: 10,
    opacity: 0.6,
  },
  actions: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    margin: '30px 0',

    '& button': {
      width: '100%',
      marginBottom: 10,
      textTransform: 'none',

      '&.cancel': {
        color: 'red',
        marginTop: 20,
      },
    },
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  droneIcon: {
    height: 30,
    width: 30,
    marginLeft: 8,
    marginRight: 8,
  },
  cameraIcon: {
    height: 25,
    width: 25,
    marginLeft: 8,
    marginRight: 8,
  },
  loadingContent: {
    height: '100%',
    width: 400,
  },
});

interface Props {
  shootingId: number;
}

function ShootingDetail({ shootingId }: Props) {
  useBlueBody();

  const intl = useIntl();
  const styles = useStyles();
  const language = useLanguage();
  const dispatch = useDispatch();

  const { token, isAdmin } = useAuth();
  const { shooting } = useShooting(token, shootingId);

  const { loading, distanceFee } = useDistanceFee(shootingId);

  if (!shooting) return null;

  const {
    state,
    listing,
    service_offers,
    shooting_params,
    shootingPackage,
    billableKms,
    pricePerBillableKm,
    totalCost,
    gstCost,
    qstCost,
    totalCostWithTax,
    scheduleddate,
  } = shooting;

  const today = moment().startOf('day');
  const isBeforeToday = moment(scheduleddate).isBefore(today);
  const isShootToday = moment(scheduleddate).isSame(today, 'day');

  const [longDistanceServiceOffer, setLongDistanceServiceOffer] = useState(null);
  const [totalCostWithDirectDrive, setTotalCostWithDirectDrive] = useState(totalCost);
  const [totalGSTWithDirectDrive, setTotalGSTWithDirectDrive] = useState(gstCost);
  const [totalQSTWithDirectDrive, setTotalQSTWithDirectDrive] = useState(qstCost);
  const [totalCostWithDirectDriveWithTax, setTotalCostWithDirectDriveWithTax] =
    useState(totalCostWithTax);
  const isEnglish = language === ENG;
  const isRecommended = state === Shooting.STATE_ENUM.recommended;
  const isDraft = state === Shooting.STATE_ENUM.draft;
  const isDelivered = Shooting.DELIVERED_STATES.includes(state);
  const isInProgress = state === Shooting.STATE_ENUM.inProgress;
  const isPostProdInProgress = Shooting.POSTPRODINPROGRESS_STATES.includes(state);
  const isCaptured = state === Shooting.STATE_ENUM.captured;
  const isUploaded = state === Shooting.STATE_ENUM.uploaded;
  const isAttentionRequired =
    !isDelivered &&
    !isInProgress &&
    !isPostProdInProgress &&
    !isCaptured &&
    !isUploaded &&
    isBeforeToday &&
    !isDraft;
  const isEditable =
    !isDelivered && !isInProgress && !isCaptured && !isPostProdInProgress && !isAttentionRequired;
  const isConfirmed = state === Shooting.STATE_ENUM.confirmed;

  const fmtBilledAt = intl.formatMessage({ id: 'shooting.detail.billedAt' });
  const fmtBookAnotherShoot = intl.formatMessage({ id: 'shooting.detail.bookAnotherShoot' });

  const getLongDistanceServiceOffer = async () => {
    const longDistance = await fetchLongDistanceServiceOffer(token, listing.broker_id);
    setLongDistanceServiceOffer(longDistance);
  };

  useEffect(() => {
    if (shooting.direct_drive) {
      getLongDistanceServiceOffer();
    }
  }, [shooting.direct_drive]);

  useEffect(() => {
    setTotalCostWithDirectDrive(_.round(totalCost + (longDistanceServiceOffer?.price || 0), 2));
    setTotalGSTWithDirectDrive(_.round(totalCostWithDirectDrive * 0.05, 2));
    setTotalQSTWithDirectDrive(_.round(totalCostWithDirectDrive * 0.09975, 2));
    setTotalCostWithDirectDriveWithTax(_.round(totalCostWithDirectDrive * 1.14975, 2));
  }, [loading, longDistanceServiceOffer, totalCostWithDirectDrive]);
  console.log('loading', loading);
  const onPingTech = () => {};

  const onCloseShootingDetail = () => {
    dispatch(hideSideBar(SIDEBAR.SHOOTING_DETAIL));
  };

  const onBookAnotherShoot = () => {
    window.location.href = `/shooting/${shootingId}?bookAnotherShoot=true`;
    onCloseShootingDetail();
  };

  const onModifyShooting = () => {
    window.location.href = `/shooting/${shootingId}`;
    onCloseShootingDetail();
  };

  const onChangeDateTime = () => {
    window.location.href = `/shooting/${shootingId}?isUpdateDate=true`;
    onCloseShootingDetail();
  };

  const onCancelShooting = () => {
    dispatch(
      showModal({
        type: MODAL.SHOOTING_CANCEL,
        data: { shootingId },
      }),
    );
    onCloseShootingDetail();
  };

  const onDeleteShooting = () => {
    dispatch(
      showModal({
        type: MODAL.DRAFT_SHOOTING_DELETE,
        data: { shootingId },
      }),
    );
    onCloseShootingDetail();
  };

  const checkIfChooseDateEnabled = () => {
    if (!shooting) {
      return false;
    }
    if (shooting.listing.address === null || shooting.listing.address === '') {
      return false;
    }
    if (shooting.listing.room_count === 0) {
      return false;
    }
    if (!shooting.broker_present && !shooting.client_present && shooting.key_box === null) {
      return false;
    }
    if (
      shooting.shooting_params.shooting_package.id === undefined &&
      shooting.service_offers.length === 0
    ) {
      return false;
    }
    if (
      shooting.shooting_params.shooting_package.id === undefined &&
      shooting.service_offers.filter((sso) => {
        return sso.can_be_standalone === true;
      }).length === 0
    ) {
      return false;
    }
    return true;
  };

  const displayDateButton = () => {
    if (state === Shooting.STATE_ENUM.draft && !checkIfChooseDateEnabled()) {
      return false;
    }
    if (!isShootToday || isRecommended || isAdmin) {
      return true;
    }
    return false;
  };

  const renderHeader = () => {
    return (
      <>
        <h2>{listing.cityAddress}</h2>
        <Button className={styles.close} onClick={onCloseShootingDetail}>
          <CloseIcon />
        </Button>
      </>
    );
  };

  const renderClientInfo = () => {
    const hasClientInfo =
      !!shooting_params?.client_name ||
      !!shooting_params?.client_email ||
      !!shooting_params?.client_phone;

    return (
      <Box className={styles.infoBlock}>
        {hasClientInfo ? (
          <Typography className={styles.infoBlockTitle}>
            {intl.formatMessage({ id: 'shooting.detail.clientInfo' })}
          </Typography>
        ) : null}
        {shooting_params?.client_name ? (
          <Box className={styles.infoItem}>
            <Typography className={styles.bolder}>{shooting_params.client_name}</Typography>
          </Box>
        ) : null}
        {shooting_params?.client_email ? (
          <Box className={styles.infoItem}>
            <Typography className={styles.bold}>{shooting_params.client_email}</Typography>
          </Box>
        ) : null}
        {shooting_params?.client_phone ? (
          <Box className={styles.infoItem}>
            <Typography className={styles.bold}>{shooting_params.client_phone}</Typography>
          </Box>
        ) : null}
      </Box>
    );
  };

  const renderScheduledDate = () => {
    if (!scheduleddate) return null;
    return (
      <Box className={styles.infoBlock}>
        <Typography className={styles.infoBlockTitle}>
          {intl.formatMessage({ id: 'shooting.detail.scheduledDate' })}
        </Typography>

        <Box className={styles.infoItem}>
          <Typography className={styles.bold}>
            {`${moment(scheduleddate).format('MM/DD/YYYY HH:mm')}`}
          </Typography>
        </Box>
      </Box>
    );
  };

  const renderPackage = () => {
    if (!shootingPackage?.id) return null;

    return (
      <Box className={styles.infoBlock}>
        <Typography className={styles.infoBlockTitle}>
          {intl.formatMessage({ id: 'shooting.detail.package' })}
        </Typography>

        <Box className={styles.infoItem}>
          <Typography className={styles.bold}>
            {isEnglish ? shootingPackage.display_name_en : shootingPackage.display_name_fr}
          </Typography>
          {shootingPackage.is_drone_photo ? (
            <Box className={styles.row}>
              <Typography>
                {shootingPackage.hdr_photo_count - shootingPackage.drone_photo_count}
              </Typography>
              <img className={styles.cameraIcon} src={images.shooting} alt='shooting' />
              <Typography>{shootingPackage.drone_photo_count}</Typography>
              <img className={styles.droneIcon} src={images.drone} alt='drone' />
            </Box>
          ) : (
            <Box className={styles.row}>
              <Typography className={styles.bold}>{shootingPackage.hdr_photo_count}</Typography>
              <img className={styles.cameraIcon} src={images.shooting} alt='shooting' />
            </Box>
          )}
          <Typography className={styles.value}>{`$${shootingPackage.price}`}</Typography>
        </Box>
      </Box>
    );
  };

  const renderAdditionalServices = () => {
    if (!service_offers || !service_offers.length) return null;

    return (
      <Box className={styles.infoBlock}>
        <Typography className={styles.infoBlockTitle}>
          {intl.formatMessage({ id: 'shooting.detail.services' })}
        </Typography>
        {service_offers.map((m: ServiceOffer) => {
          if (m.is_child === true) {
            return null;
          }
          return (
            <Box className={styles.infoItem} key={m.id}>
              <Typography className={styles.bold}>
                {isEnglish ? m.serviceOffer?.display_name : m.serviceOffer?.display_name_fr}
              </Typography>
              <Typography className={styles.value}>{`$${m.serviceOffer.price}`}</Typography>
            </Box>
          );
        })}
      </Box>
    );
  };

  const renderAmenities = () => {
    const _amenities = shooting_params?.getAmenities() || [];
    if (!_amenities || !_amenities.length) {
      return null;
    }

    const displayAmenities = _amenities
      .map((m: any) => {
        return isEnglish ? m.name : m.name_fr;
      })
      .join(', ');

    return (
      <Box className={styles.infoBlock}>
        <Typography className={styles.infoBlockTitle}>
          {intl.formatMessage({ id: 'shooting.detail.amenities' })}
        </Typography>
        <Box className={styles.infoItem}>
          <Typography className={styles.bold}>{displayAmenities}</Typography>
        </Box>
      </Box>
    );
  };

  const renderDistanceFee = () => {
    if (!distanceFee) return null;

    return (
      <Box className={styles.infoBlock}>
        <Typography className={styles.infoBlockTitle}>
          {intl.formatMessage({ id: 'shooting.detail.distanceFee' })}
        </Typography>
        <Box className={styles.infoItem}>
          <Typography className={styles.bold}>
            {`${billableKms} KMs ${fmtBilledAt} ${pricePerBillableKm}`}
          </Typography>
          <Typography className={styles.value}>{`$${distanceFee}`}</Typography>
        </Box>
      </Box>
    );
  };

  const renderDirectDriveFee = () => {
    if (!shooting.direct_drive) return null;
    return (
      <Box>
        <Typography className={styles.infoBlockTitle}>
          {intl.formatMessage({ id: 'shooting.detail.directDriveFee' })}
        </Typography>
        <Box className={styles.infoItem}>
          <Typography className={styles.bold}>
            {isEnglish
              ? longDistanceServiceOffer?.display_name
              : longDistanceServiceOffer?.display_name_fr}
          </Typography>
          <Typography className={styles.value}>{`$${longDistanceServiceOffer?.price}`}</Typography>
        </Box>
      </Box>
    );
  };
  const renderCost = () => {
    return (
      <Box className={styles.infoBlock}>
        <hr />
        <Box className={styles.infoItem}>
          <Typography>{intl.formatMessage({ id: 'shooting.detail.subTotal' })}</Typography>
          <Typography className={styles.value}>{`$${totalCostWithDirectDrive}`}</Typography>
        </Box>

        <hr />
        <Box className={styles.infoItem}>
          <Typography>{intl.formatMessage({ id: 'shooting.detail.gst' })}</Typography>
          <Typography className={styles.value}>{`$${totalGSTWithDirectDrive}`}</Typography>
        </Box>
        <Box className={styles.infoItem}>
          <Typography>{intl.formatMessage({ id: 'shooting.detail.qst' })}</Typography>
          <Typography className={styles.value}>{`$${totalQSTWithDirectDrive}`}</Typography>
        </Box>

        <hr style={{ height: 1, backgroundColor: 'black' }} />
        <Box className={styles.infoItem}>
          <Typography className={styles.bold}>
            {intl.formatMessage({ id: 'shooting.detail.total' })}
          </Typography>
          <Typography className={styles.bold}>{`$${totalCostWithDirectDriveWithTax}`}</Typography>
        </Box>
      </Box>
    );
  };

  const renderMessages = () => {
    return (
      <>
        {isInProgress ? (
          <Box className={styles.messageBox}>
            <PhotoCameraOutlinedIcon className={styles.icon} />
            <Typography className={styles.message}>
              {intl.formatMessage({ id: 'shooting.detail.shootingBeingCaptured' })}
            </Typography>
          </Box>
        ) : null}

        {(isCaptured || isPostProdInProgress) && !isInProgress ? (
          <Box className={styles.messageBox}>
            <ImageOutlinedIcon className={styles.icon} />
            <Typography className={styles.message}>
              {intl.formatMessage({ id: 'shooting.detail.mediaBeingCreated' })}
            </Typography>
          </Box>
        ) : null}

        {isAttentionRequired ? (
          <Box className={styles.messageBox}>
            <ErrorOutlineRoundedIcon className={styles.icon} />
            <Typography className={styles.message}>
              {intl.formatMessage({ id: 'shooting.detail.attentionRequired' })}
            </Typography>
          </Box>
        ) : null}
      </>
    );
  };

  const renderActions = () => {
    if (!isEditable) {
      return (
        <Box className={styles.actions}>
          <Button color='primary' variant='outlined' onClick={onBookAnotherShoot}>
            {fmtBookAnotherShoot}
          </Button>
        </Box>
      );
    }
    return (
      <Box className={styles.actions}>
        {isConfirmed && isShootToday ? (
          <Button color='primary' variant='contained' onClick={onPingTech}>
            {intl.formatMessage({ id: 'shooting.detail.pingTech' })}
          </Button>
        ) : null}

        {(!isRecommended && !isShootToday) || isAdmin ? (
          <Button color='primary' variant='outlined' onClick={onModifyShooting}>
            {state === Shooting.STATE_ENUM.draft
              ? intl.formatMessage({ id: 'shooting.detail.modifyDraft' })
              : intl.formatMessage({ id: 'shooting.detail.modifyShooting' })}
          </Button>
        ) : null}

        {displayDateButton() ? (
          <Button color='primary' variant='outlined' onClick={onChangeDateTime}>
            {isRecommended || state === Shooting.STATE_ENUM.draft
              ? intl.formatMessage({ id: 'shooting.detail.selectDateTime' })
              : intl.formatMessage({ id: 'shooting.detail.changeDate' })}
          </Button>
        ) : null}
        {(!isShootToday || isRecommended || isAdmin) && state !== Shooting.STATE_ENUM.draft ? (
          <Button className='cancel' variant='outlined' onClick={onCancelShooting}>
            {isRecommended
              ? intl.formatMessage({ id: 'shooting.detail.anotherShootingDeclined' })
              : intl.formatMessage({ id: 'shooting.detail.cancelShoot' })}
          </Button>
        ) : null}
        {state === Shooting.STATE_ENUM.draft ? (
          <Button className='cancel' variant='outlined' onClick={onDeleteShooting}>
            {intl.formatMessage({ id: 'shooting.detail.deleteDraft' })}
          </Button>
        ) : null}
      </Box>
    );
  };

  return (
    <Drawer open anchor='right' onClose={onCloseShootingDetail}>
      {loading === LOADING.STARTED ? (
        <div className={styles.loadingContent}>
          <HvPleaseWait backgroundColor={COLOR.WHITE} />
        </div>
      ) : (
        <Box className={styles.shootingDetail}>
          {renderHeader()}
          {renderClientInfo()}
          {renderScheduledDate()}
          {renderPackage()}
          {renderAdditionalServices()}
          {renderAmenities()}
          {renderDistanceFee()}
          {renderDirectDriveFee()}
          {renderCost()}
          {renderMessages()}
          {renderActions()}
        </Box>
      )}
    </Drawer>
  );
}

ShootingDetail.propTypes = {
  shootingId: PropTypes.number.isRequired,
};

export default ShootingDetail;
